import React, {
  PropsWithChildren,
  ReactElement,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Trans } from 'react-i18next';

import { isGraphQLErrorResponse } from '../api/emptyApi';
import {
  CurrentAccountFragment,
  useFetchCurrentAccountQuery
} from '../api/graphqlApi';
import { useAppSelector } from '../hooks/redux';

export type AccountContextValue = {
  account: CurrentAccountFragment | null;
  error?: unknown;
  isUnauthorized: boolean;
  isReady: boolean;
};

export const AccountContext = React.createContext<
  AccountContextValue | undefined
>(undefined);

export const AccountProvider = ({
  children
}: PropsWithChildren): ReactElement => {
  const { isAuthenticated, user } = useAppSelector(state => state.auth);
  const [refetching, setRefetching] = useState(false);
  const lastUserIdRef = useRef(user?.id);

  const skip = !isAuthenticated;
  const {
    data: account,
    error,
    refetch,
    isLoading,
    isUninitialized
  } = useFetchCurrentAccountQuery(undefined, {
    skip,
    selectFromResult: ({ data, ...rest }) => ({
      data: data?.currentAccount,
      ...rest
    })
  });

  useLayoutEffect(() => {
    if (
      isAuthenticated &&
      lastUserIdRef.current &&
      lastUserIdRef.current !== user?.id
    ) {
      lastUserIdRef.current = user?.id;
      setRefetching(true);
      void (async () => {
        try {
          await refetch();
        } finally {
          setRefetching(false);
        }
      })();
    }
  }, [isAuthenticated, refetch, user?.id]);

  const isUnauthorized = Boolean(
    error && isGraphQLErrorResponse(error) && error.statusCode === 401
  );

  const isReady = !(isLoading || (!skip && isUninitialized) || refetching);

  if (!isReady) {
    return <Trans i18nKey="loading" />;
  }

  const value = {
    account: isAuthenticated ? account ?? null : null,
    isUnauthorized,
    isReady
  };
  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
};
